import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Avatar,
  Skeleton,
  Drawer,
  IconButton,
} from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { getStats, getTracks } from '../../services/postMeal';

interface NutritionDashboardProps {
  mutationProcess: boolean;
}

const NutritionDashboard: React.FC<NutritionDashboardProps> = ({ mutationProcess }) => {
  const dailyGoal = 2200;
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setUserEmail(localStorage.getItem('userEmail'));
  }, []);

  const {
    data: tracks,
    isLoading: isTracksLoading,
    isError: isTracksError,
  } = useQuery({
    queryKey: ['track', userEmail],
    queryFn: () => getTracks(userEmail!),
    enabled: !!userEmail,
  });

  const {
    data: stats,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useQuery({
    queryKey: ['stats', userEmail],
    queryFn: () => getStats(userEmail!),
    enabled: !!userEmail,
  });

  // Show loading skeleton if userEmail is missing or tracks are loading
  if (!userEmail || isTracksLoading) {
    return (
      <Box p={3} bgcolor='#fafafa'>
        <Skeleton variant='rectangular' animation='wave' height={120} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} sm={4} key={item}>
              <Skeleton variant='rectangular' animation='wave' height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Typography variant='h5' gutterBottom>
          Meal Intakes
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} key={item}>
              <Skeleton variant='rectangular' animation='wave' height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Show error if either query fails
  if (isTracksError || isStatsError) {
    return (
      <Box p={3}>
        <Typography color='error'>Error loading data</Typography>
      </Box>
    );
  }

  // Calculate totals safely
  const totalConsumed = Array.isArray(tracks)
    ? tracks.reduce((sum: number, meal: any) => sum + (meal?.nutrition?.calories || 0), 0)
    : 0;
  const totalProgress = Math.min((totalConsumed / dailyGoal) * 100, 100);

  const totalFat = Array.isArray(tracks)
    ? tracks.reduce((sum: number, meal: any) => sum + (meal?.nutrition?.nutrients?.fat || 0), 0)
    : 0;
  const totalCarbs = Array.isArray(tracks)
    ? tracks.reduce((sum: number, meal: any) => sum + (meal?.nutrition?.nutrients?.carbs || 0), 0)
    : 0;
  const totalProtein = Array.isArray(tracks)
    ? tracks.reduce((sum: number, meal: any) => sum + (meal?.nutrition?.nutrients?.protein || 0), 0)
    : 0;
  const totalMacros = totalFat + totalCarbs + totalProtein;

  const macroPercentages = {
    Carbs: totalMacros ? (totalCarbs / totalMacros) * 100 : 0,
    Proteins: totalMacros ? (totalProtein / totalMacros) * 100 : 0,
    Fats: totalMacros ? (totalFat / totalMacros) * 100 : 0,
  };

  const macronutrients = [
    { name: 'Carbs', value: Math.round(macroPercentages.Carbs), color: '#26a69a', icon: FastfoodIcon },
    { name: 'Proteins', value: Math.round(macroPercentages.Proteins), color: '#42a5f5', icon: FitnessCenterIcon },
    { name: 'Fats', value: Math.round(macroPercentages.Fats), color: '#ffa726', icon: LocalDiningIcon },
  ];

  return (
    <Box p={3} bgcolor='#fafafa'>
      {/* Total Calories Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: '#fff',
          '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
          transition: 'all 0.3s ease',
        }}
      >
        <CardContent>
          <Box display='flex' alignItems='center'>
            <CircularProgress
              variant='determinate'
              value={totalProgress}
              size={80}
              thickness={4}
              sx={{ mr: 2, color: '#ff7043' }}
            />
            <Box>
              <Typography variant='h5' gutterBottom>
                Total Calories
              </Typography>
              <Box display='flex' alignItems='center'>
                <LocalFireDepartmentIcon sx={{ mr: 1, color: '#ff7043' }} />
                <Typography variant='h4'>{totalConsumed}</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Macronutrients Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {macronutrients.map((macro) => {
          const IconComponent = macro.icon;
          return (
            <Grid item xs={12} sm={4} key={macro.name}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  backgroundColor: '#fff',
                  '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardContent>
                  <Box display='flex' alignItems='center'>
                    <CircularProgress
                      variant='determinate'
                      value={macro.value}
                      size={60}
                      thickness={4}
                      sx={{ mr: 2, color: macro.color }}
                    />
                    <Box>
                      <Typography variant='h6' sx={{ color: macro.color }}>
                        {macro.name}
                      </Typography>
                      <Typography variant='body2' sx={{ color: macro.color }}>
                        {macro.value}%
                      </Typography>
                    </Box>
                    <Box ml='auto'>
                      <IconComponent sx={{ fontSize: 40, color: macro.color }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Stats Card or Skeleton */}
      {isStatsLoading ? (
        <Box bgcolor='#fafafa'>
          <Skeleton variant='rectangular' animation='wave' height={120} sx={{ mb: 3, borderRadius: 2 }} />
        </Box>
      ) : (
        <Card
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: 3,
            backgroundColor: '#fff',
            cursor: 'pointer',
            '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
            transition: 'all 0.3s ease',
          }}
          onClick={() => setDrawerOpen(true)}
        >
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Meal Healthiness Score
            </Typography>
            <Box display='flex' alignItems='center'>
              <Typography variant='h4' sx={{ mr: 2 }}>
                {stats?.score || 'N/A'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {stats?.healthiness || 'No description available'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Meal Intakes */}
      <Typography variant='h5' gutterBottom>
        Meal Intakes
      </Typography>
      <Grid container spacing={3}>
        {mutationProcess && (
          <Grid item xs={12}>
            <Skeleton variant='rectangular' animation='wave' height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        )}
        {Array.isArray(tracks) &&
          tracks.map((meal: any) => (
            <Grid item xs={12} key={meal.id}>
              <Card
                sx={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                  backgroundColor: '#fff',
                  '&:hover': { boxShadow: 6, transform: 'scale(1.02)' },
                  transition: 'all 0.3s ease',
                }}
              >
                <Avatar src={meal.imagePath} variant='rounded' sx={{ width: 80, height: 80, mr: 2 }} />
                <Box>
                  <Typography variant='h6'>{meal.mealName}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {meal.nutrition.calories} Calories
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Drawer for Healthiness Details */}
      <Drawer anchor='right' open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 300, sm: 400 }, p: 2 }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>Healthiness Details &amp; Advice</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box mt={2}>
            <div dangerouslySetInnerHTML={{ __html: stats?.advice || '' }} />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default NutritionDashboard;
